package com.eladfinish.bible.contest.screens.randomVerse.ui.model

import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.LinkAnnotation.Clickable
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.TextUnit
import com.eladfinish.bible.contest.screens.names.ui.colorList
import com.eladfinish.bible.contest.text.SpanType
import com.eladfinish.bible.contest.text.SpannableStringKMP

data class AnnotatedText(
    val annotatedString: AnnotatedString,
    val clicksById: Map<String, () -> Unit>,
)

fun SpannableStringKMP.toAnnotatedText(
    baseFontSize: TextUnit,
    fontFamilyResolver: (String) -> FontFamily? = { null },
): AnnotatedText {
    val rawText = charSequence.toString()
    val clicks = LinkedHashMap<String, () -> Unit>()
    var clickIndex = 0

    val annotated = buildAnnotatedString {
        append(rawText)

        spannedRangeList.forEach { range ->
            val start = range.textRange.start.coerceIn(0, rawText.length)
            val end = range.textRange.end.coerceIn(0, rawText.length)
            if (start >= end) return@forEach

            when (val span = range.spanType) {
                is SpanType.Bold -> {
                    addStyle(style = SpanStyle(fontWeight = FontWeight.Bold), start = start, end = end)
                }

                is SpanType.Underline -> {
                    addStyle(style = SpanStyle(textDecoration = TextDecoration.Underline), start = start, end = end)
                }

                is SpanType.BackgroundColor -> {
                    addStyle(style = SpanStyle(background = span.color), start = start, end = end)
                }

                is SpanType.BackgroundColorFromPalette -> {
                    val background = colorList[span.paletteIndex.mod(colorList.size)]
                    addStyle(style = SpanStyle(background = background), start = start, end = end)
                }

                is SpanType.ForegroundColor -> {
                    addStyle(style = SpanStyle(color = span.color), start = start, end = end)
                }

                is SpanType.RelativeSize -> {
                    // ABSOLUTE sizing (baseFontSize * proportion), NOT relative `.em`.
                    // On iOS/Native, relative `.em` font-size spans resolve cumulatively, so the
                    // 2nd+ ot-pasuk span compounded (0.5 × 0.5 = 0.25) and rendered far too small,
                    // while the first "א" stayed correct. Absolute sizing matches
                    // SpannableStringKmpToAnnotatedStringMapper and renders every ot-pasuk uniformly.
                    addStyle(style = SpanStyle(fontSize = baseFontSize * span.proportion), start = start, end = end)
                }

                is SpanType.CustomTypeface -> {
                    fontFamilyResolver(span.family)?.let { ff ->
                        addStyle(style = SpanStyle(fontFamily = ff), start = start, end = end)
                    }
                }

                is SpanType.ClickableLegacy -> {
                    val id = "click_${clickIndex++}"
                    clicks[id] = span.onClick

                    // We now use the built-in listener of LinkAnnotation.Clickable
                    // This handles the tap events automatically without needing manual pointerInput detection.
                    addLink(
                        clickable = Clickable(
                            tag = id,
                            linkInteractionListener = {
                                span.onClick()
                            }
                        ),
                        start = start,
                        end = end,
                    )

                    // Link style
                    addStyle(
                        style = SpanStyle(
                            color = Color.Unspecified, // Use current text color or define a specific link color
                            textDecoration = TextDecoration.Underline
                        ),
                        start = start,
                        end = end,
                    )
                }

                is SpanType.Clickable -> {

                }
            }
        }
    }

    return AnnotatedText(
        annotatedString = annotated,
        clicksById = clicks,
    )
}
